import React from 'react';
import { EADIN_STATUS_LABELS, EADIN_STATUS_STYLES, mapEadinStatus } from './receivedUtils';
import {
  BOX_STATE_META,
  PO_STATUS_META,
  RECEIVING_STATUS_META,
  TONE_BADGE,
} from './receivingStatus';

const TONE_PILL = {
  neutral: 'bg-gray-100 text-gray-600',
  ok: 'bg-green-50 text-green-700',
  warn: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  info: 'bg-blue-50 text-blue-700',
};

const TONE_DOT = {
  neutral: 'bg-gray-400',
  ok: 'bg-green-500',
  warn: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-blue-500',
};

// Rounded pill with a leading status dot, matching the Manus prototype.
export function DotPill({ tone = 'neutral', children }) {
  return (
    <span
      className={`inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-sm font-medium ${TONE_PILL[tone]}`}
    >
      <span className={`size-[6px] shrink-0 rounded-full ${TONE_DOT[tone]}`} />
      {children}
    </span>
  );
}

export function ReceivingStatusBadge({ status }) {
  const meta = RECEIVING_STATUS_META[status] || { label: status, tone: 'neutral' };
  return <DotPill tone={meta.tone}>{meta.label}</DotPill>;
}

export function EadinStatusBadge({ status }) {
  const key = typeof status === 'string' && ['open', 'closed', 'canceled', 'draft'].includes(status)
    ? status
    : mapEadinStatus(status);
  return (
    <span className={`inline-flex h-5 items-center rounded px-2 text-sm font-semibold whitespace-nowrap ${EADIN_STATUS_STYLES[key]}`}>
      {EADIN_STATUS_LABELS[key]}
    </span>
  );
}

export function StatusBadge({ status }) {
  const meta = PO_STATUS_META[status] || { label: status, tone: 'neutral' };
  return (
    <span className={`inline-flex h-5 items-center rounded px-1.5 text-sm font-semibold whitespace-nowrap ${TONE_BADGE[meta.tone]}`}>
      {meta.label}
    </span>
  );
}

export function ToneBadge({ tone, children }) {
  return (
    <span className={`inline-flex h-5 items-center gap-1 rounded px-1.5 text-sm font-semibold whitespace-nowrap ${TONE_BADGE[tone]}`}>
      {children}
    </span>
  );
}

export function BoxStateBadge({ state }) {
  const meta = BOX_STATE_META[state] || { label: state, tone: 'neutral' };
  return <ToneBadge tone={meta.tone}>{meta.label}</ToneBadge>;
}

export function VarianceChip({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-gray-400 tabular-nums">—</span>;
  }
  if (value === 0) {
    return (
      <span className="inline-flex h-5 items-center rounded bg-green-100 px-1.5 font-mono text-sm font-semibold text-green-800 tabular-nums">
        0
      </span>
    );
  }
  return (
    <span className="inline-flex h-5 items-center rounded bg-amber-100 px-1.5 font-mono text-sm font-semibold text-amber-800 tabular-nums">
      {value > 0 ? `+${value}` : value}
    </span>
  );
}

// Compact variance pill with dot, matching the Manus prototype table.
export function VarianceDot({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-gray-400 tabular-nums">—</span>;
  }
  if (value === 0) {
    return <DotPill tone="ok">0</DotPill>;
  }
  return <DotPill tone="warn">{value > 0 ? `+${value}` : value}</DotPill>;
}

// Slim segmented progress bar (received / short / remaining), like the prototype.
export function SlimBoxProgress({ boxes, className = '' }) {
  const list = boxes || [];
  const total = list.length;
  let received = 0;
  let partial = 0;
  for (const b of list) {
    const units = b.amendedUnits ?? b.handCountUnits ?? b.receivedUnits ?? 0;
    const expected = b.expectedUnits || 0;
    if (units > 0 && (expected <= 0 || units >= expected)) received += 1;
    else if (units > 0 || b.state === 'short' || b.state === 'over' || b.state === 'hand_count_pending')
      partial += 1;
  }
  const pct = (n) => (total > 0 ? `${(n / total) * 100}%` : '0%');
  return (
    <span className="inline-flex items-center gap-2" aria-hidden>
      <span className={`relative h-[6px] w-[96px] shrink-0 overflow-hidden rounded-full bg-gray-200 ${className}`}>
        {total > 0 && (
          <>
            {received > 0 && (
              <span className="absolute inset-y-0 left-0 rounded-full bg-green-500" style={{ width: pct(received) }} />
            )}
            {partial > 0 && (
              <span
                className="absolute inset-y-0 rounded-full bg-amber-500"
                style={{ left: pct(received), width: pct(partial) }}
              />
            )}
          </>
        )}
      </span>
    </span>
  );
}
